import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { EventNote, QueryStats } from '@mui/icons-material';
import EmptyState from '../common/EmptyState';
import useAutoFetch from '../../hooks/useAutoFetch';
import { getSessionAnalysis } from '../../services/analysisService';
import { AnalysisScope } from './useAnalysisScope';
import { useGradeScale } from './useGradeScale';
import TrendSections from './TrendSections';
import { Delta, ExamCountSelect, ResultBody, ScopeFilters, ResultHeading, StatStrip } from './AnalysisParts';
import FilterBar from '../common/FilterBar';
import { pct, summarise, toRows, withColors } from './trend';

/** Each subject's class average across the session's exams. */
const SubjectPerformanceView: React.FC<{ scope: AnalysisScope }> = ({ scope }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const grades = useGradeScale();
  const [examCount, setExamCount] = useState(10);
  const { sessionId, centerId } = scope;
  const allClasses = scope.raw('class') === 'all';
  const classId = allClasses ? '' : scope.classId;

  const analysis = useAutoFetch(
    sessionId ? `${sessionId}|${classId}|${examCount}` : null,
    () => getSessionAnalysis({ sessionId, classId: classId || undefined, centerId, examCount }),
  );

  const series = useMemo(() => {
    const d = analysis.data;
    if (!d) return [];
    const subjects = d.subjects.map((s) => ({
      id: s.subjectId,
      name: s.subjectName,
      points: s.examData.map((p) => ({ examId: p.examId, examName: p.examName, examDate: p.examDate, value: p.averagePercentage ?? null })),
    }));
    // The all-subjects line is the class average the server computes per exam.
    const overall = d.overallTrend.map((p) => ({ examId: p.examId, examName: p.examName, examDate: p.examDate, value: p.averagePercentage }));
    return withColors(subjects, { name: 'All subjects', points: overall }, theme.palette.text.primary);
  }, [analysis.data, theme.palette.text.primary]);

  const rows = useMemo(() => toRows(series), [series]);
  const summary = useMemo(() => summarise(rows, series), [rows, series]);

  const className = allClasses ? 'All classes' : scope.className;
  const { sessionName } = scope;
  const latest = summary.latest;

  const empty = !sessionId ? (
    <EmptyState icon={<EventNote />} title="No sessions yet" description="Create an academic session to start analysing results." />
  ) : analysis.notFound || (analysis.data && rows.length === 0) ? (
    <EmptyState
      icon={<QueryStats />}
      title={`No results for ${className} in ${sessionName} yet`}
      description="Subject averages appear once marks for a published exam are entered."
      action={{ label: 'Manage exams', icon: <EventNote />, onClick: () => navigate('/app/examination/manage') }}
    />
  ) : null;

  return (
    <>
      <FilterBar loading={analysis.loading}>
        <ScopeFilters scope={scope} allClasses />
        <ExamCountSelect value={examCount} onChange={setExamCount} />
      </FilterBar>

      <ResultBody state={analysis} empty={empty}>
        <ResultHeading title={className} subtitle={`${sessionName} · average score of every student, per subject`} />
        <StatStrip stats={[
          {
            label: 'Latest average',
            value: pct(latest?.value),
            color: latest ? grades.color(latest.value) : undefined,
            hint: latest ? `${latest.row.examName} · all subjects` : 'No scored exam yet',
          },
          { label: 'Since previous exam', value: <Delta value={summary.delta} />, hint: summary.delta == null ? 'Needs two exams' : 'Change in the average' },
          { label: 'Strongest subject', value: summary.best?.series.name ?? '—', hint: summary.best ? `Average ${pct(summary.best.value)}` : undefined },
          { label: 'Weakest subject', value: summary.worst?.series.name ?? '—', hint: summary.worst ? `Average ${pct(summary.worst.value)}` : undefined },
        ]} />

        <TrendSections rows={rows} series={series} grades={grades} />
      </ResultBody>
    </>
  );
};

export default SubjectPerformanceView;
